import * as React from "react";
import {ReactElement, useEffect} from "react";
import {IGroup} from "../Interfaces/IAllModules";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {moduleIcons} from "../Icons/Icons";
import EditModule from "./Modals/EditModule";
import {listModuleInstances} from "../../../BackendFacade/moduleCalls";
import {IModule} from "../../../BackendFacade/IModuleInstances";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {IListStaticModule} from "../../../BackendFacade/IStaticModules";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import OdxBreadcrumbs from '../../../OdxComponents/NativeOdx/Odx-Breadcrumbs'
import {odxIconSets} from '../../../OdxComponents/constants'
import OdxAreaHeader from '../../../OdxComponents/NativeOdx/Odx-AreaHeader'
import {transformOdxLayoutRef} from '../../../OdxComponents/odxLayoutRef'
import ModuleOverviewCard from '../../../OdxComponents/CustomOdx/ModuleOverviewCard'
import DeleteModule from './Modals/DeleteModule'
import AddModule from './Modals/AddModule'
import {NetworkData} from '../../../BackendFacade'
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {orderBy} from "../../../Utils/sorting";
import {simplifyForHtmlId} from "../../../Utils/transformer";

const AllModules: React.FC = () => {
    const [groupModules, setGroupModules] = React.useState<IGroup[]>([]);
    const [availableModules, setAvailableModules] = React.useState<IListStaticModule[]>([]);
    const [editModule, setEditModule] = React.useState<IModule | undefined>(undefined);
    const [deleteModule, setDeleteModule] = React.useState<IModule | undefined>(undefined);
    const [addModule, setAddModule] = React.useState<boolean>(false);
    const currentRef = React.createRef<HTMLDivElement>();
    const [toast, setToast] = React.useState(undefined)
    const baseId = 'project-modules';

    const {createErrorToast, setLoading, isLoading, createToast} = useMain();

    const handleModulesUpdate = async (response: NetworkData) => {
        await LoadModules();
        if (response.success) {
            createToast({
                duration: 5000,
                message: "Module Instance has been added successfully",
                title: "Success",
                variant: "success"
            })
        } else {
            createToast({
                message: response.message,
                title: "Error",
                isDismissable: true,
                variant: "danger",
                duration: 15000
            })
        }
        setAddModule(false);
    };

    useModuleViewTracking('projects-allmodules')

    useEffect(() => {
        LoadModules()
    }, [addModule]);

    useEffect(() => {
        transformOdxLayoutRef(currentRef.current)
    })

    const LoadModules = async () => {
        setLoading(true);

        const listToLoad = [] as Promise<void>[];

        if (!GlobalDictionary.get(HubPathRouting.currentProject.id + GlobalDictionary.MODULEINSTANCES)) {
            listToLoad.push(getExistingModules());
        } else {
            createModuleGroups();
            getExistingModules();
        }

        setAvailableModules(await GlobalDictionary.getStaticModules());

        await Promise.all(listToLoad);
        setLoading(false);
    };

    /**
     * Gets the list of all module instances from the backend
     */
    const getExistingModules = async () => {
        const id = HubPathRouting.currentProject.id
        await listModuleInstances(id).then((data) => {
            if (data.success) {
                const modules = data.data.value as IModule[]
                GlobalDictionary.set(HubPathRouting.currentProject.id + GlobalDictionary.MODULEINSTANCES, modules)
                createModuleGroups()
            } else {
                createErrorToast("Error", data.message)
            }
        })
    }

    /**
     * Creates the groups of modules and sets the state
     */
    const createModuleGroups = () => {
        const modules = GlobalDictionary.get(HubPathRouting.currentProject.id + GlobalDictionary.MODULEINSTANCES) as IModule[]
        // sort the list of modules into groups
        const groups = [] as IGroup[]
        modules.forEach((module) => {
            const group = groups.find((group) => group.name === module.group)
            if (group) {
                group.modules.push(module)
            } else {
                if (module.group === undefined || module.group === "") {
                    module.group = "Other"
                }
                groups.push({
                    name: module.group,
                    modules: [module]
                })
            }
        })
        setGroupModules(groups)
    }

    /**
     * Renders the existing modules
     */
    const getExistingModuleCards = () => {

        const getModules = (module, moduleKey) => {
            const moduleKeyId = simplifyForHtmlId(module.moduleInstanceId || module.staticModuleId || module.displayName || `module-${moduleKey}`);
            return <>
                <div id={`${baseId}-card-${moduleKeyId}`} data-odxlayout="4@desktop "
                     key={module.moduleInstanceId ?? moduleKey} className={'flex flex-col h-full HubProj_ModuleInstanceCard'}>
                    <ModuleOverviewCard
                        title={module.displayName}
                        description={availableModules.find((mod) => mod.staticModuleId === module.staticModuleId)?.description}
                        imageSrc={moduleIcons[module.staticModuleId] ? moduleIcons[module.staticModuleId] : moduleIcons['default']}
                        module={module}
                        idPrefix={`${baseId}-card-${moduleKeyId}`}
                        contextMenuActions={[
                            {
                                label: 'Edit',
                                onClick: (e: { stopPropagation: () => void; }) => {
                                    e.stopPropagation()
                                    setEditModule(module);
                                    setDeleteModule(undefined)
                                },
                                odxIcon: {iconName: 'edit', iconSet: odxIconSets.core}
                            },
                            {
                                label: 'Delete',
                                onClick: (e: { stopPropagation: () => void; }) => {
                                    e.stopPropagation()
                                    setDeleteModule(module)
                                    setEditModule(undefined)
                                },
                                odxIcon: {iconName: 'delete', iconSet: odxIconSets.core}
                            }]}
                    />
                </div>
            </>
        }

        // sort the groups alphabetically but "other" always last
        const orderedGroups = orderBy(groupModules, x => (x.name == "Other").toString(), false, x=> x.name);

        const content = orderedGroups.map((group, index) => {
            const groupId = simplifyForHtmlId(group.name || `group-${index}`);
            return <div key={index}>
                <div className={`odx-headline !text-3xl !font-bold !pt-10`} id={`${baseId}-group-${groupId}-title`}>{group.name}</div>
                <div data-odxLayout='grid vertical-start' className={'odx-padding-top-24'} id={`${baseId}-group-${groupId}-grid`}>
                    { group.modules.map((module, moduleKey) => getModules(module, moduleKey)) }
                </div>
            </div>
        });

        return <div id={`${baseId}-list`}>
            {content}
        </div>
    }

    // use memo to prevent rerendering of the existing modules
    const existingModuleCardsMemo = React.useMemo(() => getExistingModuleCards(), [groupModules])

    const NoExistingModules = () => {
        return <div id={`${baseId}-empty`}>
            <div className={`text-[20px] mt-[15px]`} id={`${baseId}-empty-message`}>No modules instances available. Order new modules</div>
        </div>
    }

    return <>
        {toast}
        {isLoading ? <></> :
            <div ref={currentRef} id={`${baseId}-view`}>

                {editModule &&
                    <EditModule state={editModule} projectId={HubPathRouting.currentProject.id}
                                isOpen={true} onSuccessFunction={handleModulesUpdate}
                                closeFunction={() => setEditModule(undefined)}
                                moduleDescription={availableModules.find((mod) => mod.staticModuleId === editModule.staticModuleId)?.description}/>}

                {deleteModule && <DeleteModule moduleTitle={deleteModule.displayName}
                                               moduleInstanceId={deleteModule.moduleInstanceId}
                                               isOpen={true} onSuccessFunction={handleModulesUpdate}
                                               closeFunction={() => setDeleteModule(undefined)}/>}

                {addModule && <AddModule isOpen={true} onSuccessFunction={handleModulesUpdate}
                                         onErrorFunction={handleModulesUpdate}
                                         closeFunction={() => setAddModule(false)}/>}

                <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>
                <OdxAreaHeader
                    odxSize={'large'}
                    text={'My Modules'}
                    odxContent={
                        <>
                        {isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id) && (
                            <odx-button
                                variant="accent"
                                size="lg"
                                type="button"
                                id={`${baseId}-add-module-btn`}
                                onClick={() => setAddModule(true)}
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                Add Module
                            </odx-button>
                        )}
                        </>}
                    className={'!pl-0 !pr-0'}
                    id={`${baseId}-header`}
                />
                {groupModules.length > 0 ?
                    <div id={`${baseId}-groups`}>
                        {existingModuleCardsMemo}
                    </div>
                    : <NoExistingModules/>}

            </div>}
    </>
}

export default AllModules;
